import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Phone, 
  Heart, 
  Baby, 
  Clock, 
  BookOpen, 
  Users, 
  Instagram,
  Menu,
  X,
  Sparkles,
  Stethoscope,
  ShieldCheck,
  MessageCircle,
  Sun,
  FileText,
  Shield,
  AlertTriangle,
  Thermometer,
  Apple,
  Wind,
  Brain,
  Smile,
  Bone,
  Siren,
  FileSearch,
  ChevronRight,
  Milk,
  Mic
} from "lucide-react";

// ElevenLabs Conversational AI agent id
const ELEVENLABS_AGENT_ID = "agent_4401kmq0jc5nf8mtpwpff947bh3p";

// Launch the ElevenLabs voice widget and start the call immediately
const startVoiceChat = () => {
  const widget = document.getElementById("projectmaa-convai");
  if (!widget) return;
  // Newer widget builds may expose an imperative method
  if (typeof widget.startConversation === "function") {
    widget.startConversation();
    return;
  }
  // Otherwise expand the launcher and click its internal "Start Call" button
  widget.setAttribute("variant", "expanded");
  let attempts = 0;
  const tryStartCall = () => {
    attempts += 1;
    const root = widget.shadowRoot;
    if (root) {
      const buttons = Array.from(root.querySelectorAll("button"));
      const callBtn = buttons.find((b) => {
        const label = ((b.getAttribute("aria-label") || "") + " " + (b.textContent || "")).toLowerCase();
        return /start|call|कॉल|शुरू/.test(label);
      });
      if (callBtn) {
        callBtn.click();
        return;
      }
    }
    if (attempts < 25) setTimeout(tryStartCall, 150);
    else console.warn("ElevenLabs widget: could not find Start Call button to auto-start the voice chat.");
  };
  setTimeout(tryStartCall, 150);
};
import TermsOfUse from "./pages/TermsOfUse";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Quick Prompts for WhatsApp
const quickPrompts = [
  {
    category: "Pregnancy & Prenatal",
    emoji: "🤰",
    prompt: "I'm in my first trimester and feeling very nauseous. What can I do?",
    color: "bg-white border-stone-200 text-emerald-800"
  },
  {
    category: "Newborn Care",
    emoji: "👶",
    prompt: "My newborn is 2 weeks old and cries a lot at night. Is this normal?",
    color: "bg-white border-stone-200 text-emerald-800"
  },
  {
    category: "Breastfeeding",
    emoji: "🍼",
    prompt: "My baby is not latching properly. How can I improve breastfeeding?",
    color: "bg-white border-stone-200 text-emerald-800"
  },
  {
    category: "Fever & Infections",
    emoji: "🌡️",
    prompt: "My child has a fever of 101°F, when should I give paracetamol?",
    color: "bg-white border-stone-200 text-emerald-800"
  },
  {
    category: "Growth & Feeding",
    emoji: "🍎",
    prompt: "My toddler used to eat everything but now eats almost nothing.",
    color: "bg-white border-stone-200 text-emerald-800"
  },
  {
    category: "Breathing & Coughs",
    emoji: "💨",
    prompt: "My child's chest is making a rattling sound. Is it a chest infection?",
    color: "bg-white border-stone-200 text-emerald-800"
  }
];

// Knowledge Base Categories
const knowledgeCategories = [
  {
    icon: "baby",
    title: "Pregnancy & Prenatal Care",
    description: "Trimester-wise guidance, nutrition, prenatal checkups, and preparing for delivery.",
    color: "pink"
  },
  {
    icon: "heart",
    title: "Newborn Care (0-3 months)",
    description: "Sleep schedules, bathing, umbilical care, jaundice, and early bonding.",
    color: "purple"
  },
  {
    icon: "milk",
    title: "Breastfeeding & Infant Feeding",
    description: "Latching techniques, milk supply, weaning, and formula feeding guidance.",
    color: "teal"
  },
  {
    icon: "apple",
    title: "Growth, Nutrition & Feeding",
    description: "Navigating appetite changes, weight concerns, and vital nutrients.",
    color: "green"
  },
  {
    icon: "thermometer",
    title: "Infections, Fevers & Vaccines",
    description: "Understanding fevers, antibiotic myths, and the truth about immunity.",
    color: "red"
  },
  {
    icon: "wind",
    title: "Breathing, Coughs & Allergies",
    description: "Managing chronic coughs, wheezing, asthma, and the impact of pollution.",
    color: "blue"
  },
  {
    icon: "stomach",
    title: "Stomach, Gut & Toilet Training",
    description: "Solutions for abdominal pain, constipation, bedwetting, and urinary health.",
    color: "yellow"
  },
  {
    icon: "brain",
    title: "Brain, Milestones & Development",
    description: "Tracking speech, walking, and understanding neurological development.",
    color: "indigo"
  },
  {
    icon: "smile",
    title: "Behaviour, Emotions & Mental Health",
    description: "Handling tantrums, screen time, anxiety, depression, and adolescent changes.",
    color: "violet"
  },
  {
    icon: "bone",
    title: "Bones, Skin & Senses",
    description: "Addressing growing pains, skin rashes, dental health, and vision/hearing concerns.",
    color: "orange"
  },
  {
    icon: "siren",
    title: "Emergencies & First Aid",
    description: "Critical, time-sensitive guidance for injuries, burns, choking, and poisoning.",
    color: "coral"
  },
  {
    icon: "file",
    title: "Understanding Lab Reports",
    description: "Demystifying common investigations, blood tests, and scans.",
    color: "slate"
  }
];

// Translations
const translations = {
  en: {
    nav: {
      about: "About",
      topics: "Knowledge Base",
      doctors: "Our Doctors",
      contact: "Contact"
    },
    hero: {
      tagline: "From Pregnancy to Growing Up",
      title: "Project MAA",
      titleHighlight: "A Mother's Guide to Good Health",
      subtitle: "Knowledge doesn't replace a parent's love—it makes that love safer.",
      description: "Project MAA is your trusted guide through the journey of childhood. We are here to help you recognise what is normal, what can be safely managed at home, what needs timely medical attention, and what is a true emergency. Every response is designed to reduce harm, dispel myths, avoid unnecessary medicines, and help you make safer decisions for your child.",
      cta: "Chat on WhatsApp",
      ctaSub: "Available 24/7 • Free Guidance",
      voiceCta: "Talk to the Agent here"
    },
    prompts: {
      title: "Try asking about...",
      subtitle: "Click any prompt to start a conversation"
    },
    about: {
      label: "About Project Maa",
      title: "A Parent's Support System",
      titleHindi: "माता-पिता का सहारा",
      description: "Project MAA is your trusted guide through the journey of childhood. We help you recognise what is normal, what can be safely managed at home, what needs timely medical attention, and what is a true emergency.",
      descriptionHindi: "प्रोजेक्ट माँ बचपन की यात्रा में आपका विश्वसनीय मार्गदर्शक है।"
    },
    features: {
      label: "Knowledge Base",
      title: "What We Cover",
      titleHindi: "हम किसमें मदद करते हैं"
    },
    doctors: {
      label: "Meet Our Doctors",
      title: "The Experts Behind MAA",
      titleHindi: "माँ के पीछे के विशेषज्ञ",
      kafeel: {
        name: "Dr. Kafeel Khan",
        role: "Pediatrician & Child Rights Activist",
        description: "A renowned pediatrician with decades of experience in child healthcare. His voice and expertise power the Project MAA AI assistant."
      },
      shabista: {
        name: "Dr. Shabista Khan",
        role: "Dentist & Public Health Activist",
        description: "A dedicated dentist, public health activist, and co-author of the book 'Maa', bringing maternal health expertise to every conversation."
      }
    },
    cta: {
      title: "Ready to Talk?",
      titleHindi: "बात करने के लिए तैयार?",
      description: "Our AI assistant is available 24/7 to answer your questions about your child's health, development, and wellbeing.",
      button: "Chat on WhatsApp",
      buttonHindi: "WhatsApp पर चैट करें"
    },
    emergency: {
      title: "EMERGENCY WARNING",
      text: "Project MAA provides guidance, not medical diagnosis. If your child is struggling to breathe, unconscious, seizing, or severely injured, do not wait for an AI response.",
      action: "Call 108 or visit an emergency room immediately."
    },
    footer: {
      rights: "© 2025 Project Maa. All rights reserved.",
      tagline: "An initiative by Dr. Kafeel Khan & Dr. Shabista Khan",
      instagram: "Follow on Instagram"
    }
  },
  hi: {
    nav: {
      about: "परिचय",
      topics: "ज्ञान आधार",
      doctors: "हमारे डॉक्टर",
      contact: "संपर्क"
    },
    hero: {
      tagline: "गर्भावस्था से बड़े होने तक",
      title: "प्रोजेक्ट माँ",
      titleHighlight: "अच्छे स्वास्थ्य के लिए माँ की गाइड",
      subtitle: "ज्ञान माता-पिता के प्यार की जगह नहीं लेता—यह उस प्यार को सुरक्षित बनाता है।",
      description: "प्रोजेक्ट माँ बचपन की यात्रा में आपका विश्वसनीय मार्गदर्शक है। हम यहाँ आपकी मदद करने के लिए हैं कि क्या सामान्य है, क्या घर पर सुरक्षित रूप से प्रबंधित किया जा सकता है, क्या समय पर चिकित्सा ध्यान देने की आवश्यकता है, और क्या वास्तविक आपातकाल है।",
      cta: "WhatsApp पर चैट करें",
      ctaSub: "24/7 उपलब्ध • मुफ्त मार्गदर्शन",
      voiceCta: "यहाँ एजेंट से बात करें"
    },
    prompts: {
      title: "इसके बारे में पूछें...",
      subtitle: "बातचीत शुरू करने के लिए किसी भी प्रॉम्प्ट पर क्लिक करें"
    },
    about: {
      label: "प्रोजेक्ट माँ के बारे में",
      title: "माता-पिता का सहारा",
      titleHindi: "A Parent's Support System",
      description: "प्रोजेक्ट माँ बचपन की यात्रा में आपका विश्वसनीय मार्गदर्शक है। हम आपको यह पहचानने में मदद करते हैं कि क्या सामान्य है, क्या घर पर सुरक्षित रूप से प्रबंधित किया जा सकता है, क्या समय पर चिकित्सा ध्यान देने की आवश्यकता है।",
      descriptionHindi: "Project MAA is your trusted guide through the journey of childhood."
    },
    features: {
      label: "ज्ञान आधार",
      title: "हम क्या कवर करते हैं",
      titleHindi: "What We Cover"
    },
    doctors: {
      label: "हमारे डॉक्टरों से मिलें",
      title: "माँ के पीछे के विशेषज्ञ",
      titleHindi: "The Experts Behind MAA",
      kafeel: {
        name: "डॉ. कफील खान",
        role: "बाल रोग विशेषज्ञ और बाल अधिकार कार्यकर्ता",
        description: "बाल स्वास्थ्य देखभाल में दशकों के अनुभव के साथ एक प्रसिद्ध बाल रोग विशेषज्ञ। उनकी आवाज़ और विशेषज्ञता प्रोजेक्ट माँ AI सहायक को शक्ति प्रदान करती है।"
      },
      shabista: {
        name: "डॉ. शबिस्ता खान",
        role: "दंत चिकित्सक और सार्वजनिक स्वास्थ्य कार्यकर्ता",
        description: "एक समर्पित दंत चिकित्सक, सार्वजनिक स्वास्थ्य कार्यकर्ता और 'माँ' पुस्तक की सह-लेखक।"
      }
    },
    cta: {
      title: "बात करने के लिए तैयार?",
      titleHindi: "Ready to Talk?",
      description: "हमारा AI सहायक आपके बच्चे के स्वास्थ्य, विकास और कल्याण के बारे में आपके सवालों का जवाब देने के लिए 24/7 उपलब्ध है।",
      button: "WhatsApp पर चैट करें",
      buttonHindi: "Chat on WhatsApp"
    },
    emergency: {
      title: "आपातकालीन चेतावनी",
      text: "प्रोजेक्ट माँ मार्गदर्शन प्रदान करता है, चिकित्सा निदान नहीं। यदि आपके बच्चे को सांस लेने में कठिनाई हो रही है, बेहोश है, दौरे पड़ रहे हैं, या गंभीर रूप से घायल है, तो AI प्रतिक्रिया का इंतजार न करें।",
      action: "तुरंत 108 पर कॉल करें या आपातकालीन कक्ष में जाएं।"
    },
    footer: {
      rights: "© 2025 प्रोजेक्ट माँ। सर्वाधिकार सुरक्षित।",
      tagline: "डॉ. कफील खान और डॉ. शबिस्ता खान की एक पहल",
      instagram: "Instagram पर फॉलो करें"
    }
  }
};

// Emergency Banner Component
const EmergencyBanner = ({ t, onHeightChange }) => {
  const bannerRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      if (bannerRef.current) onHeightChange(bannerRef.current.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [onHeightChange, t]);

  return (
    <div ref={bannerRef} className="bg-[#991B1B] text-white py-2 px-4 fixed top-0 left-0 right-0 z-[60]" data-testid="emergency-banner">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 text-center sm:text-left">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 animate-pulse" />
          <span className="font-bold text-xs">{t.emergency.title}:</span>
        </div>
        <p className="text-xs">
          {t.emergency.text} <span className="font-bold">{t.emergency.action}</span>
        </p>
      </div>
    </div>
  );
};

// Header Component
const Header = ({ lang, setLang, t, bannerHeight }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/80 backdrop-blur-xl shadow-sm border-b border-white/40' : 'bg-transparent'
      }`}
      style={{ top: bannerHeight }}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="logo">
            <img 
              src="/project-maa-logo.png"
              alt="Project Maa"
              className="h-10 w-auto"
            />
            <span className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Project <span className="text-[#0F4C3A]">MAA</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-slate-700 hover:text-[#0F4C3A] transition-colors font-semibold" data-testid="nav-about">
              {t.nav.about}
            </a>
            <a href="#topics" className="text-slate-700 hover:text-[#0F4C3A] transition-colors font-semibold" data-testid="nav-topics">
              {t.nav.topics}
            </a>
            <a href="#doctors" className="text-slate-700 hover:text-[#0F4C3A] transition-colors font-semibold" data-testid="nav-doctors">
              {t.nav.doctors}
            </a>
            
            {/* Language Toggle */}
            <button
              onClick={() => setLang(lang === 'en' ? 'hi' : 'en')}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F4C3A] text-white hover:bg-[#0B3C2E] transition-all font-bold hover:scale-105"
              data-testid="language-toggle"
            >
              <Sun className="w-4 h-4" />
              {lang === 'en' ? 'हिंदी' : 'EN'}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 rounded-full bg-[#F3EFEA]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-[#0F4C3A]" /> : <Menu className="w-6 h-6 text-[#0F4C3A]" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white rounded-3xl p-6 mt-2 mb-4 shadow-xl border border-slate-100"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-4">
              <a href="#about" onClick={() => setMobileMenuOpen(false)} className="text-slate-700 hover:text-[#0F4C3A] transition-colors font-semibold py-2">
                {t.nav.about}
              </a>
              <a href="#topics" onClick={() => setMobileMenuOpen(false)} className="text-slate-700 hover:text-[#0F4C3A] transition-colors font-semibold py-2">
                {t.nav.topics}
              </a>
              <a href="#doctors" onClick={() => setMobileMenuOpen(false)} className="text-slate-700 hover:text-[#0F4C3A] transition-colors font-semibold py-2">
                {t.nav.doctors}
              </a>
              <button
                onClick={() => { setLang(lang === 'en' ? 'hi' : 'en'); setMobileMenuOpen(false); }}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#0F4C3A] text-white font-bold"
                data-testid="mobile-language-toggle"
              >
                <Sun className="w-4 h-4" />
                {lang === 'en' ? 'हिंदी में बदलें' : 'Switch to English'}
              </button>
            </nav>
          </motion.div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ t }) => {
  const whatsappBaseUrl = "https://wa.me/918448820700";
  
  return (
    <section className="min-h-screen hero-gradient relative overflow-hidden pt-44 sm:pt-32" data-testid="hero-section">
      {/* Decorative blobs */}
      <div className="absolute top-32 right-10 w-72 h-72 bg-[#0F4C3A] rounded-full blur-3xl opacity-[0.06]" />
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-[#C86D51] rounded-full blur-3xl opacity-[0.08]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            {/* Project Maa Logo */}
            <div className="flex justify-center lg:justify-start mb-6">
              <img 
                src="/project-maa-logo.png"
                alt="Project Maa - Mother and Baby"
                className="w-48 h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 object-contain float-animation"
                data-testid="hero-logo"
              />
            </div>
            
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F4C3A] text-white text-sm font-bold mb-4">
              <Sparkles className="w-4 h-4" />
              {t.hero.tagline}
            </span>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              {t.hero.title}:
              <br />
              <span className="text-gradient-vibrant">{t.hero.titleHighlight}</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-700 mb-4 font-semibold italic">
              "{t.hero.subtitle}"
            </p>
            
            <p className="text-base text-slate-600 mb-6 leading-relaxed">
              {t.hero.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a 
                href={whatsappBaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bounce-hover inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#20bd5a] transition-all shadow-lg shadow-[#25D366]/30 cursor-pointer"
                data-testid="hero-cta"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {t.hero.cta}
              </a>
              <button
                type="button"
                onClick={startVoiceChat}
                className="bounce-hover inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0F4C3A] text-white rounded-full font-bold text-lg hover:bg-[#0c3d2e] transition-all shadow-lg shadow-[#0F4C3A]/30 cursor-pointer"
                data-testid="hero-voice-cta"
              >
                <Mic className="w-5 h-5" />
                {t.hero.voiceCta}
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-500 font-medium">{t.hero.ctaSub}</p>
          </motion.div>

          {/* Right - Quick Prompts & Chat */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
            id="widget"
          >
            <div className="relative bg-white rounded-3xl p-6 shadow-xl border border-slate-100">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>
                  {t.prompts.title}
                </h3>
                <p className="text-sm text-slate-500">{t.prompts.subtitle}</p>
              </div>
              
              {/* Quick Prompts */}
              <div className="space-y-3 mb-6">
                {quickPrompts.map((item, index) => (
                  <a
                    key={index}
                    href={`${whatsappBaseUrl}?text=${encodeURIComponent(item.prompt)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-3 rounded-xl border-2 ${item.color} hover:scale-[1.02] transition-all cursor-pointer`}
                    data-testid={`quick-prompt-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-xl">{item.emoji}</span>
                      <div className="flex-1">
                        <p className="font-bold text-sm mb-1">{item.category}</p>
                        <p className="text-xs opacity-80">"{item.prompt}"</p>
                      </div>
                      <ChevronRight className="w-4 h-4 opacity-50" />
                    </div>
                  </a>
                ))}
              </div>

              {/* Main WhatsApp Button */}
              <a 
                href={whatsappBaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-[#25D366] text-white rounded-2xl font-bold text-lg hover:bg-[#20bd5a] transition-all shadow-lg"
                data-testid="whatsapp-main-cta"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat with Dr. Kafeel Khan's AI
              </a>
              <p className="mt-2 text-center text-sm text-slate-400">+91 84488 20700</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Impact Section
const ImpactSection = () => {
  return (
    <section className="py-16 bg-[#0F4C3A]" data-testid="impact-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="uppercase tracking-[0.2em] text-xs font-bold text-white/70 mb-4 block">
            Impact
          </span>
          <div className="flex flex-col items-center">
            <span className="text-6xl sm:text-7xl lg:text-8xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              25,000+
            </span>
            <p className="text-xl sm:text-2xl text-white/90 font-semibold">
              conversations with anxious mothers since June 2026
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = ({ t }) => {
  return (
    <section id="about" className="py-16 md:py-24 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative order-2 lg:order-1"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-[#0F4C3A]/15 to-[#C86D51]/15 rounded-3xl blur-xl" />
            <img 
              src="https://customer-assets.emergentagent.com/job_mother-ai-companion/artifacts/ycdmxk66_Dr%20Kafeel%20with%20young%20mother.webp"
              alt="Dr. Kafeel Khan with a mother and child"
              className="relative rounded-3xl shadow-2xl w-full object-cover border-4 border-white"
              data-testid="about-image"
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-800 mb-4 block">
              {t.about.label}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              {t.about.title}
            </h2>
            <p className="text-lg text-[#C86D51] font-semibold mb-6">{t.about.titleHindi}</p>
            
            <p className="text-lg leading-relaxed text-slate-600 mb-4">
              {t.about.description}
            </p>
            <p className="text-base text-[#C86D51] font-medium">
              {t.about.descriptionHindi}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 px-4 py-3 bg-[#F5F2EB] rounded-xl border border-[#E5E0D8]">
                <ShieldCheck className="w-5 h-5 text-[#0F4C3A]" />
                <span className="text-sm font-bold text-[#1A5336]">Reduce Harm</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-3 bg-[#F5F2EB] rounded-xl border border-[#E5E0D8]">
                <AlertTriangle className="w-5 h-5 text-[#0F4C3A]" />
                <span className="text-sm font-bold text-[#1A5336]">Dispel Myths</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-3 bg-[#F5F2EB] rounded-xl border border-[#E5E0D8]">
                <Clock className="w-5 h-5 text-[#0F4C3A]" />
                <span className="text-sm font-bold text-[#1A5336]">24/7 Available</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-3 bg-[#F5F2EB] rounded-xl border border-[#E5E0D8]">
                <Heart className="w-5 h-5 text-[#0F4C3A]" />
                <span className="text-sm font-bold text-[#1A5336]">Safer Decisions</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Recent Queries Data
const recentQueries = [
  "My 6-year-old drinks very little water and urinates only 2-3 times daily",
  "Can lactose-free milk be given to a baby permanently?",
  "My child has high fever, what should I do?",
  "My toddler only eats biscuits with milk but refuses roti, dal, and rice",
  "My child has a cough and gets sick every time he drinks cold water",
  "My child watches screens for 6 hours and throws tantrums when I say no",
  "My baby is not latching properly, how can I improve breastfeeding?",
  "My child complains of stomach pain every morning before school"
];

// Recent Queries Section
const RecentQueriesSection = () => {
  return (
    <section className="py-12 bg-[#FAF8F5] border-y border-[#E5E0D8]" data-testid="recent-queries-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              Recent Queries
            </h2>
            <p className="text-slate-500">Real questions from real parents</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {recentQueries.map((query, index) => (
              <a
                key={index}
                href={`https://wa.me/918448820700?text=${encodeURIComponent(query)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 bg-white rounded-xl border border-stone-200 hover:border-emerald-700 hover:shadow-md transition-all cursor-pointer group"
                data-testid={`recent-query-${index}`}
              >
                <p className="text-sm text-slate-700 group-hover:text-slate-900 line-clamp-2">
                  "{query}"
                </p>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Knowledge Base Section
const KnowledgeBaseSection = ({ t }) => {
  const getIcon = (iconName) => {
    const iconProps = { className: "w-7 h-7" };
    switch (iconName) {
      case 'baby': return <Baby {...iconProps} />;
      case 'heart': return <Heart {...iconProps} />;
      case 'milk': return <Milk {...iconProps} />;
      case 'apple': return <Apple {...iconProps} />;
      case 'thermometer': return <Thermometer {...iconProps} />;
      case 'wind': return <Wind {...iconProps} />;
      case 'stomach': return <Heart {...iconProps} />;
      case 'brain': return <Brain {...iconProps} />;
      case 'smile': return <Smile {...iconProps} />;
      case 'bone': return <Bone {...iconProps} />;
      case 'siren': return <Siren {...iconProps} />;
      case 'file': return <FileSearch {...iconProps} />;
      default: return <Heart {...iconProps} />;
    }
  };

  const getColorClasses = () => {
    return 'bg-white border-stone-200 text-emerald-800 hover:border-emerald-700 hover:shadow-md';
  };

  return (
    <section id="topics" className="py-16 md:py-24 bg-[#FAF8F5]" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-800 mb-4 block">
            {t.features.label}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
            {t.features.title}
          </h2>
          <p className="text-lg text-[#C86D51] font-semibold">{t.features.titleHindi}</p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {knowledgeCategories.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`rounded-2xl p-5 border-2 ${getColorClasses(item.color)} hover:scale-[1.02] transition-all cursor-pointer`}
              data-testid={`knowledge-card-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {getIcon(item.icon)}
                </div>
                <div>
                  <h3 className="text-base font-bold mb-1" style={{ fontFamily: 'Outfit' }}>
                    {item.title}
                  </h3>
                  <p className="text-sm opacity-80">
                    {item.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Doctors Section
const DoctorsSection = ({ t }) => {
  return (
    <section id="doctors" className="py-16 md:py-24 bg-white" data-testid="doctors-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-800 mb-4 block">
            {t.doctors.label}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
            {t.doctors.title}
          </h2>
          <p className="text-lg text-[#C86D51] font-semibold">{t.doctors.titleHindi}</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-[#0F4C3A]/15 to-[#C86D51]/15 rounded-3xl blur-xl" />
            <img 
              src="https://customer-assets.emergentagent.com/job_12dae107-7c60-4e73-8bba-92952c9a6da0/artifacts/xarox2i5_Dr%20Kafeel%20Khan%20wife.jpg"
              alt="Dr. Kafeel Khan and Dr. Shabista Khan"
              className="relative rounded-3xl shadow-2xl w-full object-cover border-4 border-white"
              data-testid="doctors-image"
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Dr. Kafeel */}
            <div className="bg-[#F5F2EB] rounded-2xl p-6 border border-[#E5E0D8]" data-testid="doctor-kafeel-card">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#0F4C3A] flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>
                    {t.doctors.kafeel.name}
                  </h3>
                  <p className="text-sm text-[#0F4C3A] font-bold mb-3">{t.doctors.kafeel.role}</p>
                  <p className="text-slate-600 leading-relaxed">
                    {t.doctors.kafeel.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Dr. Shabista */}
            <div className="bg-[#F5F2EB] rounded-2xl p-6 border border-[#E5E0D8]" data-testid="doctor-shabista-card">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#C86D51] flex items-center justify-center flex-shrink-0">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>
                    {t.doctors.shabista.name}
                  </h3>
                  <p className="text-sm text-[#C86D51] font-bold mb-3">{t.doctors.shabista.role}</p>
                  <p className="text-slate-600 leading-relaxed">
                    {t.doctors.shabista.description}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ t }) => {
  return (
    <section className="py-16 md:py-24 bg-[#0F4C3A]" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>
            {t.cta.title}
          </h2>
          <p className="text-xl text-white/80 font-semibold mb-6">{t.cta.titleHindi}</p>
          
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            {t.cta.description}
          </p>

          <a 
            href="https://wa.me/918448820700"
            target="_blank"
            rel="noopener noreferrer"
            className="bounce-hover inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-[#25D366] rounded-full font-bold text-xl shadow-lg shadow-black/10 hover:shadow-xl transition-all"
            data-testid="cta-button"
          >
            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t.cta.button}
          </a>
          <p className="mt-4 text-sm text-white/70 font-medium">+91 84488 20700</p>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ t }) => {
  return (
    <footer className="py-12 bg-slate-900" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="/project-maa-logo.png"
              alt="Project Maa"
              className="h-10 w-auto"
            />
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              Project MAA
            </span>
          </div>

          {/* Tagline */}
          <p className="text-white/70 text-center">
            {t.footer.tagline}
          </p>

          {/* Social */}
          <a 
            href="https://www.instagram.com/drkafeelkhan/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#0F4C3A] rounded-full text-white hover:bg-[#0B3C2E] transition-all font-bold"
            data-testid="instagram-link"
          >
            <Instagram className="w-5 h-5" />
            {t.footer.instagram}
          </a>
        </div>

        {/* Legal Links */}
        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8">
          <Link 
            to="/terms-of-use" 
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors font-medium"
            data-testid="terms-link"
          >
            <FileText className="w-4 h-4" />
            Terms of Use
          </Link>
          <Link 
            to="/privacy-policy" 
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors font-medium"
            data-testid="privacy-link"
          >
            <Shield className="w-4 h-4" />
            Privacy Policy
          </Link>
        </div>

        {/* Powered by */}
        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col items-center gap-4">
          <div className="flex items-center gap-3" data-testid="powered-by-dil">
            <span className="text-white/50 text-sm">Powered by</span>
            <img 
              src="https://customer-assets.emergentagent.com/job_mother-ai-companion/artifacts/wcg8k8pe_Digital_Inclusion_Lab_2026_Logo-removebg-preview.png"
              alt="Digital Inclusion Lab"
              className="h-10 w-auto"
            />
          </div>
          <p className="text-white/40 text-sm">{t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  const [lang, setLang] = useState('en');
  const [bannerHeight, setBannerHeight] = useState(64);
  const t = translations[lang];

  // Load ElevenLabs script
  useEffect(() => {
    if (!document.querySelector('script[src*="elevenlabs/convai-widget-embed"]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
      script.async = true;
      script.type = 'text/javascript';
      document.body.appendChild(script);
    }
  }, []);

  return (
    <>
      <EmergencyBanner t={t} onHeightChange={setBannerHeight} />
      <Header lang={lang} setLang={setLang} t={t} bannerHeight={bannerHeight} />
      <HeroSection t={t} />
      <ImpactSection />
      <AboutSection t={t} />
      <KnowledgeBaseSection t={t} />
      <DoctorsSection t={t} />
      <RecentQueriesSection />
      <CTASection t={t} />
      <Footer t={t} />
      <elevenlabs-convai id="projectmaa-convai" agent-id={ELEVENLABS_AGENT_ID}></elevenlabs-convai>
    </>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <div className="App" data-testid="app">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
